/**
 * DateRangeSelector - Flexible week selector with single and range modes
 * 
 * Supports:
 * - Single week mode: Simple dropdown
 * - Range mode: Start week + End week with Compare button
 * - DTC mode: Calendar vs Fiscal toggle
 */

import React, { useState } from 'react';
import { Calendar, ChevronDown, TrendingUp } from 'lucide-react';
import { FiscalWeek } from '../../../shared/schema';

// Export types for DTC pages
export type ViewType = 'calendar' | 'fiscal'
export type CalendarRange = 'last7' | 'last30' | 'last90' | 'custom'

interface DateRangeSelectorProps {
  availableWeeks?: FiscalWeek[];
  selectedWeek?: number;
  onWeekChange?: (week: number) => void;
  onRangeCompare?: (startWeek: number, endWeek: number) => void;
  
  // DTC-specific props
  viewType?: ViewType
  onViewTypeChange?: (viewType: ViewType) => void
  calendarRange?: CalendarRange
  onCalendarRangeChange?: (range: CalendarRange) => void
  customStartDate?: string
  customEndDate?: string
  onCustomDateChange?: (startDate: string, endDate: string) => void
  fiscalWeek?: number
  onFiscalWeekChange?: (week: number) => void
  availableFiscalWeeks?: Array<{ value: number; label: string }>
}

type Mode = 'single' | 'range';

// Export utility function for DTC pages
export function getCalendarDays(range: CalendarRange): number {
  switch (range) {
    case 'last7': return 7
    case 'last30': return 30
    case 'last90': return 90
    case 'custom': return 0
    default: return 7
  }
}

// Export as both default and named for backward compatibility
export function DateRangeSelector({
  availableWeeks,
  selectedWeek,
  onWeekChange,
  onRangeCompare,
  viewType,
  onViewTypeChange,
  calendarRange,
  onCalendarRangeChange,
  fiscalWeek,
  onFiscalWeekChange,
  availableFiscalWeeks
}: DateRangeSelectorProps) {
  // If DTC mode (has viewType prop), render DTC selector
  if (viewType !== undefined && onViewTypeChange) {
    return <DtcDateRangeSelector 
      viewType={viewType}
      onViewTypeChange={onViewTypeChange}
      calendarRange={calendarRange || 'last7'}
      onCalendarRangeChange={onCalendarRangeChange}
      fiscalWeek={fiscalWeek}
      onFiscalWeekChange={onFiscalWeekChange}
      availableFiscalWeeks={availableFiscalWeeks}
    />
  }
  
  // Otherwise, retail mode
  const [mode, setMode] = useState<Mode>('single');
  const [startWeek, setStartWeek] = useState<number>(selectedWeek || 0);
  const [endWeek, setEndWeek] = useState<number>(selectedWeek || 0);

  const handleModeChange = (newMode: Mode) => {
    setMode(newMode);
    if (newMode === 'single') {
      // Reset to single week mode
      onWeekChange(selectedWeek);
    }
  };

  const handleCompare = () => {
    if (onRangeCompare && startWeek && endWeek) {
      onRangeCompare(startWeek, endWeek);
    }
  };

  const formatWeekLabel = (week: number) => {
    const year = Math.floor(week / 100);
    const weekNum = week % 100;
    const weekData = availableWeeks.find(w => w.fiscal_week === week);
    
    if (weekData) {
      return `Week ${weekNum}, ${year} (${weekData.start_date} - ${weekData.end_date})`;
    }
    
    return `Week ${weekNum}, ${year}`;
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
      <div className="flex items-center space-x-4">
        {/* Mode Toggle */}
        <div className="flex items-center space-x-2">
          <Calendar className="w-4 h-4 text-gray-500" />
          <select
            value={mode}
            onChange={(e) => handleModeChange(e.target.value as Mode)}
            className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="single">Single Week</option>
            <option value="range">Week Range</option>
          </select>
        </div>

        {mode === 'single' ? (
          /* Single Week Selector */
          <div className="flex-1">
            <select
              value={selectedWeek}
              onChange={(e) => onWeekChange(parseInt(e.target.value))}
              className="w-full text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {availableWeeks.map(week => (
                <option key={week.fiscal_week} value={week.fiscal_week}>
                  {formatWeekLabel(week.fiscal_week)}
                </option>
              ))}
            </select>
          </div>
        ) : (
          /* Range Mode Selectors */
          <>
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600 font-medium">From:</label>
              <select
                value={startWeek}
                onChange={(e) => setStartWeek(parseInt(e.target.value))}
                className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {availableWeeks.map(week => (
                  <option key={week.fiscal_week} value={week.fiscal_week}>
                    Week {week.fiscal_week % 100}, {Math.floor(week.fiscal_week / 100)}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600 font-medium">To:</label>
              <select
                value={endWeek}
                onChange={(e) => setEndWeek(parseInt(e.target.value))}
                className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {availableWeeks.map(week => (
                  <option key={week.fiscal_week} value={week.fiscal_week}>
                    Week {week.fiscal_week % 100}, {Math.floor(week.fiscal_week / 100)}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={handleCompare}
              disabled={!startWeek || !endWeek || startWeek > endWeek}
              className="px-4 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              Compare
            </button>
          </>
        )}
      </div>

      {/* Helper text for range mode */}
      {mode === 'range' && startWeek && endWeek && (
        <div className="mt-2 text-xs text-gray-500">
          {startWeek > endWeek ? (
            <span className="text-amber-600">Start week must be before end week</span>
          ) : (
            <span>
              Comparing {Math.abs(Math.floor(endWeek / 100) * 52 + (endWeek % 100) - (Math.floor(startWeek / 100) * 52 + (startWeek % 100))) + 1} weeks of data
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// DTC Date Range Selector Component
function DtcDateRangeSelector({
  viewType,
  onViewTypeChange,
  calendarRange,
  onCalendarRangeChange,
  fiscalWeek,
  onFiscalWeekChange,
  availableFiscalWeeks = []
}: {
  viewType: ViewType
  onViewTypeChange: (type: ViewType) => void
  calendarRange: CalendarRange
  onCalendarRangeChange?: (range: CalendarRange) => void
  fiscalWeek?: number
  onFiscalWeekChange?: (week: number) => void
  availableFiscalWeeks?: Array<{ value: number; label: string }>
}) {
  const calendarOptions = [
    { value: 'last7' as CalendarRange, label: 'Last 7 Days' },
    { value: 'last30' as CalendarRange, label: 'Last 30 Days' },
    { value: 'last90' as CalendarRange, label: 'Last 90 Days' },
  ]

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      {/* View Type Toggle */}
      <div className="flex items-center gap-3 mb-4">
        <span className="text-sm font-medium text-gray-700">View by:</span>
        <div className="inline-flex rounded-md shadow-sm">
          <button
            onClick={() => onViewTypeChange('calendar')}
            className={`px-4 py-2 text-sm font-medium rounded-l-lg border ${
              viewType === 'calendar'
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <Calendar className="w-4 h-4 inline-block mr-2" />
            Calendar
          </button>
          <button
            onClick={() => onViewTypeChange('fiscal')}
            className={`px-4 py-2 text-sm font-medium rounded-r-lg border border-l-0 ${
              viewType === 'fiscal'
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <TrendingUp className="w-4 h-4 inline-block mr-2" />
            Fiscal Week
          </button>
        </div>
      </div>

      {/* Calendar View */}
      {viewType === 'calendar' && onCalendarRangeChange && (
        <div className="flex flex-wrap gap-2">
          {calendarOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => onCalendarRangeChange(option.value)}
              className={`px-3 py-1.5 text-sm font-medium rounded border ${
                calendarRange === option.value
                  ? 'bg-indigo-50 text-indigo-700 border-indigo-300'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}

      {/* Fiscal Week View */}
      {viewType === 'fiscal' && onFiscalWeekChange && (
        <select
          value={fiscalWeek || ''}
          onChange={(e) => onFiscalWeekChange(parseInt(e.target.value, 10))}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
        >
          <option value="">Select fiscal week...</option>
          {availableFiscalWeeks.map((week) => (
            <option key={week.value} value={week.value}>
              {week.label}
            </option>
          ))}
        </select>
      )}
    </div>
  )
}

// Also export as default for convenience
export default DateRangeSelector;
